import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { RevealLine, SectionLabel } from "./Motion";

const chapters = [
  {
    no: "01",
    title: "Promoter-driven expertise",
    body: "Contracts secured through the industry relationships and bid qualifications of promoters — the Shah Group, Mr. Anant Moona and Mr. Prashant Moona — with roughly 25 years of hands-on experience in power transmission and EPC.",
  },
  {
    no: "02",
    title: "PSU & government-backed partnerships",
    body: "Primary clients are central and state government entities with strong institutional standing, giving projects assured backing and dependable execution frameworks.",
  },
  {
    no: "03",
    title: "Back-to-back execution",
    body: "We partner with large, established private EPC firms to reduce delivery risk and enhance contractual reliability across every engagement.",
  },
  {
    no: "04",
    title: "Strong forward pipeline",
    body: "Advanced contracting status — including L-1 positions and a Letter of Intent in hand — provides clear forward visibility on upcoming work.",
  },
];

function Chapter({ c, i }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const numY = useTransform(scrollYProgress, [0, 1], ["30%", "-30%"]);

  return (
    <div
      ref={ref}
      data-testid={`strength-chapter-${i}`}
      className="relative grid grid-cols-1 items-start gap-6 border-t border-white/15 py-12 lg:grid-cols-12 lg:gap-10 lg:py-16"
    >
      <div className="lg:col-span-4 overflow-hidden">
        <motion.div
          style={{ y: numY }}
          className="font-display text-7xl font-semibold leading-none text-vg-accent/25 lg:text-8xl"
        >
          {c.no}
        </motion.div>
      </div>
      <div className="lg:col-span-8">
        <h3 className="font-display text-2xl font-semibold leading-tight text-white lg:text-4xl">
          <RevealLine>{c.title}</RevealLine>
        </h3>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-5 max-w-2xl text-base leading-relaxed text-white/70 lg:text-lg"
        >
          {c.body}
        </motion.p>
      </div>
    </div>
  );
}

export default function Manifesto() {
  return (
    <section
      id="strengths"
      className="relative overflow-hidden bg-vg-primary py-24 lg:py-36"
      data-testid="strengths-section"
    >
      <div className="grain absolute inset-0 opacity-[0.06]" />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <SectionLabel dark>03 — Why Venus Green</SectionLabel>
        <h2 className="mt-8 max-w-3xl font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
          <RevealLine>Strengths that de-risk</RevealLine>
          <RevealLine delay={0.08}>every project we take on.</RevealLine>
        </h2>

        <div className="mt-16">
          {chapters.map((c, i) => (
            <Chapter key={c.no} c={c} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
